import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import useCollections from '../../hooks/useCollections';
import useScrollEnter from '../../hooks/useScrollEnter';
import { useFormatUrlGenerator } from '../../hooks/useUrlGenerator';
import { createSerieRow } from '../../lib/blocks';

import { useBrand, useDefaultBrand } from '../../contexts/SiteContext';
import { typesSections } from '../../routes';
import CollectionsList from '../lists/CollectionsList';
import RowTitle from '../partials/RowTitle';
import HomePageBlocks from './HomePageBlocks';

import styles from '../../styles/pages/videos-page.module.css';

export interface VideosPageProps {
    brand?: string | null;
    type?: string;
    current?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
}

const VideosPage = ({
    brand: brandSlug = null,
    type: typeSlug = 'videos',
    current = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: VideosPageProps) => {
    const type = typesSections.find(({ id, path }) => id === typeSlug || path === typeSlug);
    const { id: typeId, query: typeQuery } = type || {};

    const url = useUrlGenerator();
    const formatUrl = useFormatUrlGenerator();
    const hasBrand = brandSlug !== null;
    const { handle: defaultBrandHandle = 'urbania' } = useDefaultBrand();
    const brand = useBrand(brandSlug, !hasBrand ? defaultBrandHandle : null);
    const { handle: brandHandle = null } = brand || {};

    const { items: collectionsRows } = useCollections({
        placement_only: `${typeId}_collections_rows_${brandHandle}`,
    });

    const isVideos = typeId === 'videos';
    const isPodcast = typeId === 'podcasts';

    const seriesRow = {
        type: 'collections-row',
        presentation: 'grid',
        title: (
            <RowTitle
                url={
                    isPodcast
                        ? url('podcasts.all', { brand: brandSlug })
                        : url('series', { brand: brandSlug })
                }
                withoutViewAllButton={isPodcast}
            >
                {isPodcast ? (
                    <FormattedMessage defaultMessage="Nos balados" description="Section title" />
                ) : (
                    <FormattedMessage
                        defaultMessage="Séries en primeur"
                        description="Section title"
                    />
                )}
            </RowTitle>
        ),
        count: 16,
        query: {
            type: isPodcast ? 'serie_podcast' : 'serie',
            placement: `${typeId}_collections_${brandHandle}`,
            brand: brandHandle !== 'urbania' ? brandHandle : null,
        },
        loading: 'lazy',
        cardImageLoading: 'lazy',
        cardType: 'vertical',
        cardRatio: isPodcast ? 'square' : 'vertical',
        cardTheme: 'rounded',
        cardWidth: 150,
        // autoColumns: true,
        // cardWithout: ['text', 'type-icon'],
        cardWithout: ['text'],
        cardWith: ['category'],
        className: styles.section,
        uniqueDocuments: false,
    };

    const blocks = [
        isPodcast
            ? {
                  role: 'block',
                  type: 'header_featured_sidebar',
                  brand,
                  featuredCardTheme: 'over-video',
                  featuredCardWith: ['play-button'],
                  featuredCardLinkQuery: {
                      autoplay: true,
                  },
                  cardType: 'horizontal',
                  cardTheme: 'box',
                  cardWithout: [
                      'category',
                      'author',
                      'time',
                      'date',
                      //   'type-icon',
                  ],
                  cardWith: ['episode'],
                  cardRatio: 'square',
                  cardSize: 'medium',
                  count: 7,
                  cardWidth: 300,
                  autoColumns: true,
                  className: styles.featuredBlock,
                  innerClassName: styles.featuredInner,
                  mainClassName: classNames([styles.featuredMain, contentClassName]),
                  cardClassName: styles.featuredCard,
                  sidebarClassName: styles.featuredSidebar,
                  query: {
                      brand: brandHandle !== 'urbania' ? brandHandle : null,
                      placement: `${typeId}_featured_${brandHandle}`,
                      ...typeQuery,
                  },
                  sidebar: (
                      <div>
                          <RowTitle
                              url={url('podcasts.all', {
                                  brand: brandSlug,
                              })}
                              className={styles.title}
                          >
                              Nos balados
                          </RowTitle>
                          <CollectionsList
                              {...seriesRow}
                              autoColumns
                              cardWidth={150}
                              count={6}
                              className={styles.sidebar}
                          />
                      </div>
                  ),
                  uniqueDocuments: true,
              }
            : {
                  role: 'block',
                  type: 'header_featured_list',
                  brand,
                  cardTheme: 'over-video',
                  cardWithout: ['category', 'author', 'time', 'date'],
                  cardWith: ['play-button', 'episode'],
                  count: 1,
                  className: classNames([styles.featuredBlock, contentClassName]),
                  innerClassName: styles.featuredInner,
                  cardClassName: styles.featuredCard,
                  cardLinkQuery: {
                      autoplay: true,
                  },
                  query: {
                      brand: brandHandle !== 'urbania' ? brandHandle : null,
                      placement: `${typeId}_featured_${brandHandle}`,
                      ...typeQuery,
                  },
                  uniqueDocuments: true,
              },
        {
            type: 'documents-row',
            presentation: 'grid',
            title: (
                <RowTitle url={url(`${typeId}.all`, { brand: brandSlug })}>
                    {isPodcast ? (
                        <FormattedMessage
                            defaultMessage="Derniers épisodes"
                            description="Section title"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Vidéos récentes"
                            description="Section title"
                        />
                    )}
                </RowTitle>
            ),
            count: 6,
            query: {
                brand: brandHandle !== 'urbania' ? brandHandle : null,
                placement: `${typeId}_featured_${brandHandle}`,
                ...typeQuery,
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: isPodcast ? 'horizontal' : 'vertical',
            cardRatio: isPodcast ? 'square' : 'wide',
            cardTheme: isPodcast ? 'box' : 'rounded',
            // cardSize: isPodcast ? 'small' : 'medium',
            cardWidth: isPodcast ? 280 : 150,
            autoColumns: isPodcast,
            cardWithout: [
                !isPodcast ? 'time' : null,
                'author',
                // 'type-icon',
            ].filter((n) => n !== null),
            cardWith: ['episode'],
            className: styles.section,
            uniqueDocuments: true,
        },

        {
            type: 'documents-row',
            presentation: 'grid',
            title: (
                <RowTitle>
                    <FormattedMessage
                        defaultMessage="Populaire en ce moment"
                        description="Section title"
                    />
                </RowTitle>
            ),
            count: 6,
            query: {
                brand: brandHandle !== 'urbania' ? brandHandle : null,
                ...typeQuery,
            },
            hook: 'popular',
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: isPodcast ? 'horizontal' : 'vertical',
            cardRatio: isPodcast ? 'square' : 'wide',
            cardTheme: isPodcast ? 'box' : 'rounded',
            // cardSize: isPodcast ? 'small' : 'medium',
            cardWidth: isPodcast ? 280 : 150,
            autoColumns: isPodcast,
            cardWithout: [
                !isPodcast ? 'time' : null,
                'author',
                // 'type-icon',
            ].filter((n) => n !== null),
            cardWith: ['episode'],
            className: styles.section,
            uniqueDocuments: true,
        },

        {
            type: 'documents-row',
            presentation: 'grid',
            title: (
                <RowTitle>
                    <FormattedMessage
                        defaultMessage="Continuer à regarder"
                        description="Section title"
                    />
                </RowTitle>
            ),
            count: 12,
            placeholderCount: 6,
            hook: 'continue',
            query: {
                brand: brandHandle !== 'urbania' ? brandHandle : null,
                ...typeQuery,
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: 'vertical',
            cardRatio: 'wide',
            cardTheme: 'rounded',
            cardWidth: 150,
            // cardWithout: ['time', 'author', 'type-icon'],
            cardWithout: ['time', 'author'],
            cardWith: ['episode'],
            className: styles.section,
        },
        !isPodcast && brandHandle === 'urbania'
            ? {
                  type: 'collections-row',
                  presentation: 'grid',
                  title: (
                      <RowTitle url={url('series', { brand: brandSlug })}>
                          <FormattedMessage
                              defaultMessage="Séries télévisées"
                              description="Section title"
                          />
                      </RowTitle>
                  ),
                  count: 12,
                  query: {
                      type: 'serie',
                      brand: brandHandle !== 'urbania' ? brandHandle : null,
                      placement_only: 'videos_productions_urbania',
                  },
                  loading: 'lazy',
                  cardImageLoading: 'lazy',
                  cardType: 'vertical',
                  cardRatio: 'vertical',
                  cardTheme: 'rounded',
                  cardWidth: 300,
                  cardSize: 'medium',
                  cardWithout: ['text'],
                  cardWith: ['category'],
                  className: styles.section,
                  uniqueDocuments: false,
              }
            : null,
        !isPodcast && brandHandle === 'urbania'
            ? {
                  type: 'collections-row',
                  presentation: 'grid',
                  title: (
                      <RowTitle>
                          <FormattedMessage
                              defaultMessage="Sur les plateformes"
                              description="Section title"
                          />
                      </RowTitle>
                  ),
                  count: 12,
                  query: {
                      type: 'serie',
                      brand: brandHandle !== 'urbania' ? brandHandle : null,
                      placement_only: 'videos_broadcasts_urbania',
                  },
                  loading: 'lazy',
                  cardImageLoading: 'lazy',
                  cardType: 'vertical',
                  cardRatio: 'vertical',
                  cardTheme: 'rounded',
                  cardWidth: 300,
                  cardSize: 'medium',
                  // autoColumns: true,
                  //   cardWithout: ['text', 'type-icon'],
                  cardWithout: ['text'],
                  cardWith: ['category', 'broadcast'],
                  className: styles.section,
                  uniqueDocuments: false,
              }
            : null,
        !isPodcast ? seriesRow : null,
        ...(collectionsRows || []).map(({ slug }) =>
            createSerieRow(
                slug,
                isPodcast
                    ? {
                          className: styles.section,
                          uniqueDocuments: false,
                      }
                    : {
                          className: styles.section,
                          uniqueDocuments: false,
                      },
            ),
        ),
        ...(isVideos && brandHandle === 'urbania'
            ? [
                  {
                      label: 'Documentaire',
                      query: {
                          format: 'documentaire',
                      },
                      url: formatUrl(
                          {
                              slug: 'documentaire',
                          },
                          'videos',
                      ),
                  },
                  {
                      label: 'Entrevues',
                      query: {
                          format: 'entrevue',
                      },
                      url: formatUrl(
                          {
                              slug: 'entrevue',
                          },
                          'videos',
                      ),
                  },
                  {
                      label: 'Culture',
                      query: {
                          category: 'culture',
                      },
                      url: url('section', {
                          brand: null,
                          section: 'culture',
                          type: 'videos',
                      }),
                  },
              ].map(({ label, url, query }) => ({
                  type: 'documents-row',
                  presentation: 'grid',
                  title: <RowTitle url={url}>{label}</RowTitle>,
                  count: 12,
                  query: {
                      brand: brandHandle !== 'urbania' ? brandHandle : null,
                      ...typeQuery,
                      ...query,
                  },
                  loading: 'lazy',
                  cardImageLoading: 'lazy',
                  cardType: isPodcast ? 'horizontal' : 'vertical',
                  cardRatio: isPodcast ? 'square' : 'wide',
                  cardTheme: isPodcast ? 'box' : 'rounded',
                  // cardSize: isPodcast ? 'small' : 'medium',
                  cardWidth: isPodcast ? 280 : 150,
                  autoColumns: isPodcast,
                  cardWithout: [
                      !isPodcast ? 'time' : null,
                      'category',
                      'author',
                      // 'type-icon',
                  ].filter((n) => n !== null),
                  cardWith: ['episode'],
                  className: classNames([styles.section, styles.marginTop]),
                  uniqueDocuments: true,
              }))
            : []),

        {
            type: 'documents-row',
            presentation: 'grid',
            title: (
                <RowTitle url={url(`${typeId}.all`, { brand: brandSlug })}>
                    {isPodcast ? (
                        <FormattedMessage
                            defaultMessage="Plus de balados"
                            description="Section title"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Plus de vidéos"
                            description="Section title"
                        />
                    )}
                </RowTitle>
            ),
            count: 24,
            query: {
                brand: brandHandle !== 'urbania' ? brandHandle : null,
                ...typeQuery,
            },
            loading: 'lazy',
            cardImageLoading: 'lazy',
            cardType: isPodcast ? 'horizontal' : 'vertical',
            cardRatio: isPodcast ? 'square' : 'wide',
            cardTheme: isPodcast ? 'box' : 'rounded',
            // cardSize: isPodcast ? 'small' : 'medium',
            cardWidth: isPodcast ? 280 : 150,
            autoColumns: isPodcast,
            cardWithout: [
                !isPodcast ? 'time' : null,
                'author',
                // 'type-icon',
            ].filter((n) => n !== null),
            cardWith: ['episode'],
            className: classNames([styles.section, styles.marginTop]),
            uniqueDocuments: true,
        },
    ].filter((it) => it !== null);

    useScrollEnter({
        disabled: !current,
        onEnter,
        onLeave,
    });

    return (
        <HomePageBlocks
            current={current}
            brand={brand}
            blocks={blocks}
            header={false}
            adInterval={4}
            theme={typeId}
            pageType={typeId}
            withoutBumper
            className={classNames([styles.container, { [styles.isPodcast]: isPodcast }, className])}
            blocksClassName={styles.blocks}
        />
    );
};

export default VideosPage;
